import { getTechRoomColor, getTechRoomName } from '@/models/techRooms'
import { compose, ifEmpty, intersection } from './immutable'

import Cookie from 'js-cookie'
import Overlay from 'ol/Overlay'

export const extensionIcon = file => [
  [/\.pdf$/, 'extension-pdf'],
  [/\.(xls|xlsx)$/, 'extension-excel'],
  [/\.(folder)$/, 'extension-folder'],
  [/\.(doc|docx)$/, 'extension-word']
].find(([when]) => when.test(file || ''))?.[1] || 'extension'

/**
  * @param {number} x
  * @param {number} y
  * @param {DOMRect} rect
  * @param {number} indent
  */
export const belongToElementRectWithIndent = (x, y, rect, indent) => x >= rect.x - indent && x <= rect.x + rect.width + indent && y >= rect.y - indent && y <= rect.y + rect.height + indent

const values = {}

export const set = (key, value, { cookie = false, storage = false, session = false } = {}) => {
  values[key] = value

  if (cookie) Cookie.set(key, value)
  if (storage) localStorage.setItem(key, value)
  if (session) sessionStorage.setItem(key, value)
  
  return true
};

export const get = (key, defaultValue, { cookie = false, storage = false, session = false } = {}) => {
  if (cookie) return Cookie.get(key) ?? defaultValue
  if (storage) return ifEmpty(localStorage.getItem(key), defaultValue)
  if (session) return ifEmpty(sessionStorage.getItem(key), defaultValue)

  return values[key] ?? defaultValue
};

export const forget = (key, { cookie = false, storage = false, session = false, domain } = {}) => {
  delete values[key]

  if (cookie) Cookie.remove(key, { domain })
  if (storage) localStorage.removeItem(key)
  if (session) sessionStorage.removeItem(key)
    
  return true
};

const updates = {}

export const defer = (id, f, d = 2000) => {
  id = id || ''

  const i = setTimeout(() => {
    f()
    updates[id] = false
  }, d)

  updates[id] && clearTimeout(updates[id])
  updates[id] = i
}

export const defineFilterField = ({ type, key, placeholder, label, group, options, props, from, to, under, loading, disabled } = {}) => ({
  type,
  propName: key,
  placeholder,
  label,
  group,
  options,
  props,
  from,
  to,
  under,
  loading,
  disabled
})

export const selectTreeNodeById = (ref, id) => {
  id && ref.setCurrentKey(id)
}

export const expandTreeNodeById = (ref, id) => {
  id && ref.store.nodesMap[id] && (ref.store.nodesMap[id].expanded = true)
  id && expandTreeNodeById(ref, ref.store.nodesMap[id]?.parent?.data?.id)
}

export const injectCommonFieldProps = ({ defaultName, defaultLabel, defaultPlaceholder } = {}) => ({
  independent: { type: Boolean, default: false },
  name: { type: String, default: defaultName },
  label: { type: String, default: defaultLabel },
  placeholder: { type: String, default: defaultPlaceholder },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  errors: { type: Array, default: () => [] }
})

export const injectSelectFieldProps = () => ({
  asKey: { type: Boolean, default: false },
  multiple: { type: Boolean, default: false },
  items: { type: Array, default: null }
})

export const createTechRoomOverlay = (map, feature) => {
  const element = document.createElement('div')
  const color = document.createElement('span')
  const text = document.createElement('span')

  element.className = 'f-y-center bg-rock text-white _p-0.5 rounded shadow-md fs-0.8'
  color.className = 'wh-0.75 rounded-sm _mr-0.5'
  color.style.backgroundColor = getTechRoomColor(feature.get('techRoom'))

  text.innerText = getTechRoomName(feature.get('techRoom'))

  element.append(color, text)

  const tooltip = new Overlay({
    element,
    offset: [0, -16],
    positioning: 'bottom-center',
    stopEvent: false,
    insertFirst: false
  })
  
  const getPosition = (center, coords) => {
    const x = center[0]
    const y = coords[0].reduce((maxY, [, y]) => maxY < y ? y : maxY, 0)
    
    return [x, y]
  }
  
  const geometry = feature.getGeometry()
  
  tooltip.setPosition(getPosition(geometry.getInteriorPoint().getCoordinates(), geometry.getCoordinates()))
  tooltip.set('id', feature.get('techRoom').id)
  tooltip.set('name', getTechRoomName(feature.get('techRoom')))
  
  // remove on click
  // element.onclick = removeOverlay.bind(null, tooltip)

  map.addOverlay(tooltip)

  return tooltip
}

export const createErrorOverlay = (map, feature, errors) => {
  const element = document.createElement('div')
  element.className = 'bg-rock text-red-1 _p-0.5 rounded shadow-md fs-0.8'

  // build all errors to single string
  element.innerText = compose(
    x => x.join('\n'),
    x => x.length > 1 ? x.map(x => '- ' + x) : x,
    x => Object.values(x).flat()
  )(errors) 

  const tooltip = new Overlay({
    element,
    offset: [0, -16],
    positioning: 'bottom-center',
    stopEvent: false,
    insertFirst: false
  })

  tooltip.setPosition(feature.getGeometry().getInteriorPoint().getCoordinates())
  tooltip.set('id', feature.get('id'))
  tooltip.set('name', feature.get('name'))

  // remove on click
  element.onclick = removeOverlay.bind(null, tooltip)

  map.addOverlay(tooltip)

  return tooltip
} 

export const removeOverlay = x => {
  x.getElement().remove()
  x.getMap()?.removeOverlay?.(x)
}

export const matchRouteWithDefinition = (route, to, { nameStricted = true } = {}) => {
  const { name, params } = to || {}

  const serialize = (x = {}) => Object.entries(x || {})
    .map(([k, v]) => [k, v].join(':'))

  const decompose = (x = '') => x.split('.')

  const nameDecomposed = decompose(name)
  const paramsSerialized = serialize(params)

  const r = (
    nameStricted 
      ? name === route.name
      : intersection(nameDecomposed, decompose(route.name)).length === nameDecomposed.length
  ) && intersection(paramsSerialized, serialize({ ...route.params })).length === paramsSerialized.length

  return r
}

/**
 * Функция для получения координат отрезка, параллельного заданному, относительно реперной точки
 */
export const getParallelSegment = ([A, B], d, O) => {
  const [ xA, yA ] = A // Координаты первой точки отрезка
  const [ xB, yB ] = B // Координаты второй точки отрезка
  
  let clockwise = false // Поворот по часовой стрелке или против
  
  if (O) {
    const [ xO, yO ] = O // Координаты точки, относительно которой необходимо получить параллельный отрезок

    const vOA = { x: xA - xO, y: yA - yO } // Координаты вектора OA
    const vOB = { x: xB - xO, y: yB - yO } // Координаты вектора OB
    const z = vOA.x * vOB.y - vOA.y * vOB.x // 3-я координата из векторного произведения, указывающая с какой стороны лежит точка O

    clockwise = z > 0 // Если координата отрицательная - поворачиваем единичный вектор против часовой стрелки, если положительная - по часовой
  }

  // Длина заданного отрезка
  const distance = Math.sqrt(Math.pow(xB - xA, 2) + Math.pow(yB - yA, 2))

  // Находим единичный вектор для вектора AB для определения направления
  const v1 = { x: (xB - xA) / distance, y: (yB - yA) / distance }
  // Поворачиваем единичный вектор на 90 градусов относительно точки A, чтобы найти направление для вектора на котором будет лежать первая точка (A1) параллельного отрезка A1B1
  const v2 = clockwise ? { x: v1.y, y: -v1.x } : { x: -v1.y, y: v1.x }
  // Имея единичный вектор v2 вдоль отрезка AA1, умножаем покоординатно на расстояние (d) до параллельного отрезка AA1, чтобы получить вектор AA1 
  const v3 = { x: v2.x * d, y: v2.y * d }

  // Имея вектор АА1, получаем координаты конечных точек, лежащих на отрезках AA1 и BB1 
  const A1 = [ xA + v3.x, yA + v3.y ] 
  const B1 = [ xB + v3.x, yB + v3.y ] 

  return [ B1, A1 ]
}
